import React, { useState } from "react";
import { useCategories } from "../../hooks/useCategories";
import CategoryModal from "../../common/CategoryModal";
import { TableStatus } from "../../components/TableStatus";
import ConfirmationModal from "../../common/ConfirmationModal";

export default function CategoryTablePage() {
    const {
        categories,
        isLoading,
        isError,
        error,
        createCategory,
        updateCategory,
        deleteCategory,
        isDeleting
    } = useCategories();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    const activeCount = categories.filter(c => c.status === "Active" || !c.status).length;
    const inactiveCount = categories.length - activeCount;

    const handleOpenModal = (category = null) => {
        setEditingCategory(category);
        setIsModalOpen(true);
    };

    const handleModalSubmit = async (formData) => {
        try {
            if (editingCategory) {
                await updateCategory({ id: editingCategory._id, data: formData });
            } else {
                await createCategory(formData);
            }
            setIsModalOpen(false);
        } catch (err) {
            console.error("Failed to save category", err);
        }
    };

    const handleDelete = async () => {
        if (!categoryToDelete) return;
        await deleteCategory(categoryToDelete._id);
        setCategoryToDelete(null);
    };

    return (
        <>
            <div className="card-header bg-light d-flex justify-content-between align-items-center p-3">
                <div>
                    <h4 className="mb-0 text-primary-emphasis fw-bold">Product Categories</h4>
                    <p className="text-muted small mb-0">Manage your product hierarchy and organization</p>
                </div>
                <button className="btn btn-success" onClick={() => handleOpenModal()}>
                    <i className="fas fa-plus mr-2"></i> Add Category
                </button>
            </div>

            <div className="row g-3 mb-1 px-3 py-2">
                <div className="col-md-3">
                    <div className="card shadow-sm border-0 py-2">
                        <div className="card-body d-flex align-items-center">
                            <div className="rounded-3 bg-primary bg-opacity-10 p-3 mr-3 text-primary">
                                <i className="fas fa-folder-open fa-lg"></i>
                            </div>
                            <div>
                                <h6 className="text-muted mb-0 small fw-bold">Total</h6>
                                <h4 className="mb-0 fw-bold">{categories?.length || 0}</h4>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card shadow-sm border-0 py-2">
                        <div className="card-body d-flex align-items-center">
                            <div className="rounded-3 bg-success bg-opacity-10 p-3 mr-3 text-success">
                                <i className="fas fa-check-circle fa-lg"></i>
                            </div>
                            <div>
                                <h6 className="text-muted mb-0 small fw-bold">Active</h6>
                                <h4 className="mb-0 fw-bold">{activeCount}</h4>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card shadow-sm border-0 py-2">
                        <div className="card-body d-flex align-items-center">
                            <div className="rounded-3 bg-danger bg-opacity-10 p-3 mr-3 text-danger">
                                <i className="fas fa-times-circle fa-lg"></i>
                            </div>
                            <div>
                                <h6 className="text-muted mb-0 small fw-bold">Inactive</h6>
                                <h4 className="mb-0 fw-bold">{inactiveCount}</h4>
                            </div>
                        </div>
                    </div>
                </div>
              
            </div>

            <div className="card shadow-sm border-0">
                <div className="card-header bg-white py-3">
                    <h5 className="mb-0 text-dark fw-bold">Category List</h5>
                </div>
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="ps-4">Category</th>
                                    <th> Image</th>
                                    <th>Slug</th>
                                    <th>Status</th>
                                    <th className="text-center pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <TableStatus
                                    status={isLoading ? "loading" : isError ? "failed" : "succeeded"}
                                    error={error}
                                    dataLength={categories?.length}
                                    colSpan={5}
                                />

                                {!isLoading && categories?.map((item) => (
                                    <tr key={item._id}>
                                        <td className="ps-4">
                                            <span className="fw-bold text-dark">{item.title}</span>
                                        </td>
                                        <td>
                                            <img src={item?.imageUrl} alt="category" width={80} height={80} className="rounded" style={{ objectFit: "cover" }} />

                                        </td>
                                        <td>
                                            <code className="small text-primary bg-light px-2 py-1 rounded">
                                                {item.slug || item.title?.toLowerCase().replace(/\s+/g, '-')}
                                            </code>
                                        </td>

                                        <td>
                                            <span className={`badge rounded-pill ${item.status === 'Inactive' ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'}`}>
                                                {item.status || "Active"}
                                            </span>
                                        </td>
                                        <td className="text-center pe-4">
                                            <div className="d-flex justify-content-center gap-2">
                                                <button
                                                    className="btn btn-sm btn-outline-primary px-3 mr-2"
                                                    onClick={() => handleOpenModal(item)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => setCategoryToDelete(item)}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <CategoryModal
                show={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleModalSubmit}
                initialData={editingCategory}
            />

            <ConfirmationModal
                show={categoryToDelete !== null}
                onClose={() => setCategoryToDelete(null)}
                onConfirm={handleDelete}
                title="Delete Category"
                confirmText="Delete"
                confirmButtonVariant="danger"
                isLoading={isDeleting}
            >
                Are you sure you want to delete the category <strong>{categoryToDelete?.title}</strong>?
                This action cannot be undone.
            </ConfirmationModal>
        </>
    );
}